import { useEffect, useState } from 'react';
import {
  ArrowRight,
  Eye,
  GraduationCap,
  LoaderCircle,
  Pencil,
  ShieldCheck,
  UserPlus,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import {
  acceptCollaborationInvite,
  ensureSession,
  getCollaborationInvite,
  type SessionSnapshot,
} from '../lib/api';
import type { CollaborationInvitePreview } from '../types/note';
import { AuthDialog } from './AuthDialog';

export function CollaborationInvitePage() {
  const { token = '' } = useParams();
  const [preview, setPreview] = useState<CollaborationInvitePreview>();
  const [session, setSession] = useState<SessionSnapshot>();
  const [authOpen, setAuthOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    void ensureSession()
      .then(setSession)
      .catch(cause => setError(cause instanceof Error ? cause.message : '协作邀请暂时无法打开'));
  }, [token]);

  useEffect(() => {
    if (!session || session.user.isAnonymous) {
      setPreview(undefined);
      return;
    }
    let cancelled = false;
    setError(undefined);
    void getCollaborationInvite(token)
      .then(invite => {
        if (!cancelled) setPreview(invite);
      })
      .catch(cause => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : '协作邀请暂时无法打开');
      });
    return () => { cancelled = true; };
  }, [session, token]);

  const accept = async () => {
    if (!session || session.user.isAnonymous) {
      setAuthOpen(true);
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const result = await acceptCollaborationInvite(token);
      window.location.assign(`/?note=${encodeURIComponent(result.noteId)}&shared=1`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '加入协作失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="invite-shell">
      <header className="invite-brand"><GraduationCap /><strong>Aulivis</strong><span>多人笔记</span></header>
      <section className="invite-card">
        {!session ? (
          error ? (
            <div className="invite-error">
              <span>!</span>
              <h1>无法加入这篇笔记</h1>
              <p>{error}</p>
              <a href="/">返回笔记工作台</a>
            </div>
          ) : (
            <div className="dialog-loading"><LoaderCircle className="spin" /> 正在验证协作邀请…</div>
          )
        ) : session.user.isAnonymous ? (
          <>
            <div className="invite-visual">
              <span className="invite-orbit" />
              <UserPlus />
            </div>
            <span className="eyebrow">账号内协作</span>
            <h1>你收到一份笔记协作邀请</h1>
            <p className="invite-security"><ShieldCheck /> 登录前不会读取或展示笔记标题、所有者和内容。</p>
            <button className="primary-button invite-accept" onClick={() => setAuthOpen(true)}>
              <UserPlus /> 登录后查看邀请
            </button>
          </>
        ) : !preview ? (
          error ? (
            <div className="invite-error">
              <span>!</span>
              <h1>无法加入这篇笔记</h1>
              <p>{error}</p>
              <a href="/">返回笔记工作台</a>
            </div>
          ) : (
            <div className="dialog-loading"><LoaderCircle className="spin" /> 正在读取协作邀请…</div>
          )
        ) : (
          <>
            <div className="invite-visual">
              <span className="invite-orbit" />
              {preview.role === 'editor' ? <Pencil /> : <Eye />}
            </div>
            <span className="eyebrow">{preview.owner.displayName || '一位同学'} 邀请你</span>
            <h1>{preview.note.title}</h1>
            <div className={`invite-role is-${preview.role}`}>
              {preview.role === 'editor' ? <Pencil /> : <Eye />}
              <span>
                <strong>{preview.role === 'editor' ? '共同编辑' : '只读查看'}</strong>
                <small>{preview.role === 'editor'
                  ? '你的修改会与其他人实时同步并安全合并'
                  : '可以阅读和导出，不会改动原笔记'}</small>
              </span>
            </div>
            <button className="primary-button invite-accept" onClick={() => void accept()} disabled={busy}>
              {busy ? <LoaderCircle className="spin" /> : <ArrowRight />}
              {busy ? '正在加入…' : '加入这篇笔记'}
            </button>
            <p className="invite-security"><ShieldCheck /> 只有接受邀请的正式账号能访问；所有者可随时撤销权限。</p>
            {error && <p className="inline-error" role="alert">{error}</p>}
          </>
        )}
      </section>
      {authOpen && session && (
        <AuthDialog
          session={session}
          onSession={next => {
            setSession(next);
            setAuthOpen(false);
          }}
          onClose={() => setAuthOpen(false)}
        />
      )}
    </main>
  );
}
